import * as React from "react"

import Dialog from "./Dialog"
import {
  CONSENT_VALUES,
  ensureAnalyticsLoaded,
  getAnalyticsConsent,
  setAnalyticsConsent,
  trackPageView,
} from "../analytics"

export default function CookieNotice() {
  const [consent, setConsent] = React.useState(null)
  const [policyOpen, setPolicyOpen] = React.useState(false)
  const [resetNotice, setResetNotice] = React.useState("")

  React.useEffect(() => {
    setConsent(getAnalyticsConsent())
  }, [])

  const onAccept = () => {
    setAnalyticsConsent(CONSENT_VALUES.accepted)
    setConsent(CONSENT_VALUES.accepted)
    ensureAnalyticsLoaded()
    trackPageView(window.location.pathname)
  }

  const onReject = () => {
    setAnalyticsConsent(CONSENT_VALUES.rejected)
    setConsent(CONSENT_VALUES.rejected)
  }

  const onChangeChoice = () => {
    if (typeof window === "undefined") return
    try {
      window.localStorage.removeItem("blackfold_cookie_consent_v1")
    } catch {
      // ignore
    }
    setConsent(null)
    setResetNotice("Cookie preference removed.")
  }

  const shouldShow = consent !== CONSENT_VALUES.accepted && consent !== CONSENT_VALUES.rejected

  return (
    <>
      {shouldShow ? (
        <div className="fixed bottom-4 right-4 z-50 max-w-xs rounded-lg border border-white/15 bg-black/95 p-4 text-sm text-white shadow-2xl">
          <div className="leading-snug">
            We use Google Analytics for basic page views and to understand which products are selected.
          </div>
          <div className="mt-3 flex items-center justify-end gap-2">
            <button
              type="button"
              className="rounded-md border border-white/15 bg-transparent px-3 py-1.5 text-xs text-white hover:border-white/25"
              onClick={() => setPolicyOpen(true)}
            >
              Policy
            </button>
            <button
              type="button"
              className="rounded-md border border-white/15 bg-transparent px-3 py-1.5 text-xs text-white hover:border-white/25"
              onClick={onReject}
            >
              Reject
            </button>
            <button
              type="button"
              className="rounded-md bg-white px-3 py-1.5 text-xs font-medium text-black hover:bg-white/90"
              onClick={onAccept}
            >
              Accept
            </button>
          </div>
        </div>
      ) : null}

      <Dialog open={policyOpen} title="Cookie & Analytics Policy" onClose={() => setPolicyOpen(false)}>
        <div className="space-y-4 text-sm text-slate-200">
          <div className="space-y-2">
            <div className="text-base font-medium text-white">What we use</div>
            <div>
              If you accept, we use Google Analytics (GA4) to measure basic page views and to understand which products
              are selected/added to cart.
            </div>
          </div>

          <div className="space-y-2">
            <div className="text-base font-medium text-white">What data may be processed</div>
            <div>
              This may include information like pages you visit, approximate location, device/browser information, and
              interactions such as adding an item to your cart. We do not knowingly collect sensitive personal data.
            </div>
          </div>

          <div className="space-y-2">
            <div className="text-base font-medium text-white">Who provides analytics</div>
            <div>
              Google Analytics is provided by Google (typically Google Ireland Limited for users in the EEA/UK, and
              Google LLC). Google may process usage data on our behalf.
            </div>
          </div>

          <div className="space-y-2">
            <div className="text-base font-medium text-white">Legal basis</div>
            <div>
              We only enable analytics after you give consent by clicking Accept.
            </div>
          </div>

          <div className="space-y-2">
            <div className="text-base font-medium text-white">Retention & transfers</div>
            <div>
              Analytics data retention is configured in our Google Analytics settings. Depending on your location,
              analytics data may be processed outside your country (including potentially outside the EEA/UK).
            </div>
          </div>

          <div className="space-y-2">
            <div className="text-base font-medium text-white">Choices</div>
            <div>
              You can accept or reject analytics cookies. If you reject, Google Analytics will not be loaded and no
              analytics events will be sent.
            </div>
          </div>

          <div className="space-y-2">
            <div className="text-base font-medium text-white">How to change your choice</div>
            <div>
              You can withdraw consent at any time. Use the Change choice button below to reset your preference and
              show the consent notice again.
            </div>
          </div>

          <div className="space-y-2">
            <div className="text-base font-medium text-white">Cart storage</div>
            <div>
              We store your cart items in your browser (local storage) so your cart persists between visits.
            </div>
          </div>

          <div className="space-y-2">
            <div className="text-base font-medium text-white">Contact</div>
            <div>
              If you have questions about analytics or privacy, contact us via Instagram:{" "}
              <a
                href="https://www.instagram.com/blackfoldpodcast/"
                target="_blank"
                rel="noreferrer"
                className="underline"
              >
                @BlackFoldPodcast
              </a>
              .
            </div>
          </div>

          <div className="flex items-center justify-end gap-2 pt-2">
            {resetNotice ? (
              <div className="mr-auto text-xs text-slate-300">{resetNotice}</div>
            ) : null}
            <button
              type="button"
              className="rounded-md border border-slate-800 bg-slate-900/40 px-3 py-1.5 text-sm text-slate-100 hover:border-slate-700"
              onClick={onChangeChoice}
            >
              Change choice
            </button>
            <button
              type="button"
              className="rounded-md border border-slate-800 bg-slate-900/40 px-3 py-1.5 text-sm text-slate-100 hover:border-slate-700"
              onClick={() => setPolicyOpen(false)}
            >
              Close
            </button>
          </div>
        </div>
      </Dialog>
    </>
  )
}
